import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api, errMessage } from "../lib/api";

// Reusable 4-digit Parent PIN guard. Wrap any parent-portal route
// (/opportunities, /billing, settings) — children in child mode cannot pass
// without the PIN. First use walks the parent through setting one.
// One successful verify per browser session.
export const ParentPinGuard = ({ children }) => {
  const navigate = useNavigate();
  const [ok, setOk] = useState(() => sessionStorage.getItem("parent_pin_ok") === "1");
  const [hasPin, setHasPin] = useState(null);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ok) return;
    api.get("/auth/parent-pin/status")
      .then(({ data }) => setHasPin(data.set))
      .catch(() => setHasPin(false));
  }, [ok]);

  if (ok) return children;
  if (hasPin === null) return null;

  const pass = () => { sessionStorage.setItem("parent_pin_ok", "1"); setOk(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be 4 digits");
    setBusy(true);
    try {
      if (hasPin) {
        await api.post("/auth/parent-pin/verify", { pin });
        pass();
      } else {
        if (pin !== confirm) { setBusy(false); return toast.error("PINs don't match"); }
        await api.post("/auth/parent-pin", { pin });
        toast.success("Parent PIN set");
        pass();
      }
    } catch (err) { toast.error(errMessage(err)); setPin(""); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6"
      role="dialog" aria-modal="true" aria-label="Parent PIN required"
      data-testid="parent-pin-guard">
      <div className="w-full max-w-sm rounded-3xl bg-white/85 backdrop-blur-xl border border-white/30 shadow-2xl p-8 space-y-4">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-2xl bg-slate-800 text-white flex items-center justify-center">
            <Lock className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-xl font-bold font-display text-slate-900">Grown-ups only</h2>
            <p className="text-sm text-slate-600">
              {hasPin ? "Enter your 4-digit Parent PIN" : "Create a 4-digit Parent PIN to protect this area"}
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" inputMode="numeric" maxLength={4} autoFocus
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            aria-label="Parent PIN" placeholder="••••"
            className="text-center text-2xl h-14 bg-white tracking-[0.5em]"
            data-testid="parent-pin-input" />
          {!hasPin && (
            <Input type="password" inputMode="numeric" maxLength={4}
              value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              aria-label="Confirm PIN" placeholder="Confirm PIN"
              className="text-center text-2xl h-14 bg-white tracking-[0.5em]"
              data-testid="parent-pin-confirm" />
          )}
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1 h-12 rounded-xl"
              onClick={() => navigate("/dashboard")} data-testid="parent-pin-cancel">
              Go back
            </Button>
            <Button type="submit" disabled={busy}
              className="flex-1 h-12 rounded-xl bg-slate-800 hover:bg-slate-700 text-white"
              data-testid="parent-pin-submit">
              {hasPin ? "Unlock" : "Set PIN"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
